import { EVENT_RECORDS_PAGE_SIZE_DEFAULT, EVENT_RECORD_TYPES, type EventRecordType } from "@seagames/contracts";
import { z } from "zod";
import { cleanText, idParam, pageParam, sizeParam, textParam } from "../nocs/list-params";

/**
 * Records: the tab and both tabs' filters live in the URL
 * (`/records?tab&q&sport&discipline&event&type&active&page&size` for the
 * book, `msport&mtype&mpage` for the marks) — Back, a reload and a copied link
 * show the same page. A bad value is dropped, never an error page.
 */

export const RECORDS_TABS = ["book", "marks"] as const;
export type RecordsTab = (typeof RECORDS_TABS)[number];

const activeParam = z.enum(["active", "inactive"]).optional().catch(undefined);
const typeParam = z.enum(EVENT_RECORD_TYPES).optional().catch(undefined);

export const recordsSearch = z.object({
	/** Absent = the book. */
	tab: z.enum(RECORDS_TABS).optional().catch(undefined),
	q: textParam,
	sport: idParam,
	/** Only with `sport`. */
	discipline: idParam,
	/** Only with `sport`. */
	event: idParam,
	type: typeParam,
	active: activeParam,
	page: pageParam,
	size: sizeParam,
	// The marks tab.
	msport: idParam,
	mtype: typeParam,
	mpage: pageParam,
});
export type RecordsSearch = z.infer<typeof recordsSearch>;

// ─── The book's filter card ──────────────────────────────────────────────────

export interface BookDraft extends Record<string, unknown> {
	q: string;
	sport: string;
	discipline: string;
	event: string;
	type: EventRecordType | "";
	active: "active" | "inactive" | "";
}

export const EMPTY_BOOK_DRAFT: BookDraft = { q: "", sport: "", discipline: "", event: "", type: "", active: "" };

export const bookDraftFrom = (s: RecordsSearch): BookDraft => ({
	q: s.q ?? "",
	sport: s.sport ?? "",
	discipline: s.sport ? (s.discipline ?? "") : "",
	event: s.sport ? (s.event ?? "") : "",
	type: s.type ?? "",
	active: s.active ?? "",
});

/** Draft → URL: empties dropped, discipline / event only with their sport, the page starts over; the size and the marks tab stay. */
export function bookSearchFrom(d: BookDraft, keep: RecordsSearch): RecordsSearch {
	return {
		tab: keep.tab,
		q: cleanText(d.q),
		sport: d.sport || undefined,
		discipline: d.sport && d.discipline ? d.discipline : undefined,
		event: d.sport && d.event ? d.event : undefined,
		type: d.type || undefined,
		active: d.active || undefined,
		size: keep.size,
		msport: keep.msport,
		mtype: keep.mtype,
		mpage: keep.mpage,
	};
}

export const bookFilterCount = (s: RecordsSearch): number =>
	[s.q, s.sport, s.sport ? s.discipline : undefined, s.sport ? s.event : undefined, s.type, s.active].filter((v) => v !== undefined).length;

export const bookPaging = (s: RecordsSearch) => ({ page: s.page ?? 1, pageSize: s.size ?? EVENT_RECORDS_PAGE_SIZE_DEFAULT });

/** `GET /records` for one games — snake_case, defaults left out. */
export interface RecordsParams {
	games_id: string;
	search?: string;
	sport_id?: string;
	discipline_id?: string;
	event_id?: string;
	record_type?: EventRecordType;
	active?: "true" | "false";
	page: number;
	page_size: number;
}

export function recordsParams(gamesId: string, s: RecordsSearch): RecordsParams {
	const { page, pageSize } = bookPaging(s);
	return {
		games_id: gamesId,
		search: s.q,
		sport_id: s.sport,
		discipline_id: s.sport ? s.discipline : undefined,
		event_id: s.sport ? s.event : undefined,
		record_type: s.type,
		active: s.active === "active" ? "true" : s.active === "inactive" ? "false" : undefined,
		page,
		page_size: pageSize,
	};
}

// ─── The marks tab ───────────────────────────────────────────────────────────

export interface MarksDraft extends Record<string, unknown> {
	sport: string;
	type: EventRecordType | "";
}

export const EMPTY_MARKS_DRAFT: MarksDraft = { sport: "", type: "" };

export const marksDraftFrom = (s: RecordsSearch): MarksDraft => ({ sport: s.msport ?? "", type: s.mtype ?? "" });

export function marksSearchFrom(d: MarksDraft, keep: RecordsSearch): RecordsSearch {
	return { ...keep, msport: d.sport || undefined, mtype: d.type || undefined, mpage: undefined };
}

export const marksFilterCount = (s: RecordsSearch): number => [s.msport, s.mtype].filter((v) => v !== undefined).length;

export interface RecordMarksParams {
	games_id: string;
	sport_id?: string;
	record_type?: string;
	page: number;
	page_size: number;
}

export const marksParams = (gamesId: string, s: RecordsSearch): RecordMarksParams => ({
	games_id: gamesId,
	sport_id: s.msport,
	record_type: s.mtype,
	page: s.mpage ?? 1,
	page_size: EVENT_RECORDS_PAGE_SIZE_DEFAULT,
});
